import { CSSProperties } from "react";
import {
  ActionButton,
  CommandBarButton,
  DefaultButton,
  IButtonStyles,
  IContextualMenuItem,
  IStyle,
  PrimaryButton,
} from "@fluentui/react";
import { css, IButton as ICayoButton, IIcon, themeUtils } from "cayo.ui";
import { IButton, IActionButton } from "@cayo/shared/api/types/schema";
import { getButtonThemeStyles } from "@cayo/shared/components/Form/renderers/renderers.buttons";
import IAuxRenderParams from "@cayo/shared/components/schemes/types/AuxRenderProps";
import { ICommandBarButton, IRunnableButton } from "../../../api/schema.api";
import RunnableButton from "../../Buttons/RunnableButton";

export const renderButtonContent = (text?: string, image?: string) => {
  return (
    <>
      {image && <img alt={text} className={css.mr6} src={image} />}
      {text}
    </>
  );
};

const button = (
  annotation: IButton,

  auxParams: IAuxRenderParams
): JSX.Element => {
  return (
    <DefaultButton
      iconProps={themeUtils.getIconProps(annotation.icon)}
      // style={themeUtils.getButtonStyle(annotation)}
      styles={getButtonThemeStyles(auxParams.theme.getButtonThemeStyles(annotation))}
      disabled={annotation.disabled}
      key={annotation.name}
      id={annotation.id || annotation.name}
      ariaLabel={annotation.id || annotation.name}
      onClick={async (e: any) => {
        e.stopPropagation();
        if (annotation.action) {
          await auxParams.actionBuilder.buildAction(annotation.action!, {
            signals: auxParams.signals,
          });
        } else if (annotation.ajaxAction) {
          const response = await auxParams.actionBuilder.buildAjaxAction(annotation.ajaxAction);
          if (auxParams?.handleResponse && annotation.ajaxAction.response) {
            await auxParams?.handleResponse(annotation.ajaxAction.response, response);
          }
        }
      }}
    >
      {renderButtonContent(annotation.text, annotation.image)}
    </DefaultButton>
  );
};

const submitbutton = (
  annotation: IButton,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  auxParams: IAuxRenderParams
): JSX.Element => {
  return (
    <PrimaryButton
      type="submit"
      iconProps={themeUtils.getIconProps(annotation.icon)}
      style={themeUtils.getButtonStyle(annotation)}
      disabled={annotation.disabled}
      key={annotation.name}
      id={annotation.id || annotation.name}
      data-testid="submit-btn"
      ariaLabel={"Submit Button"}
    >
      {renderButtonContent(annotation.text, annotation.image)}
    </PrimaryButton>
  );
};

const actionbutton = (
  annotation: IActionButton,

  auxParams: IAuxRenderParams
): JSX.Element => {
  const { style } = getButtonVisibilityStyle(annotation?.permittedRole, auxParams?.userRoles);
  const styles = getButtonStyles(annotation);
  const themeStyle = themeUtils.getButtonStyle(annotation as ICayoButton);

  let button = (
    <ActionButton
      disabled={annotation.disabled}
      key={annotation.name}
      ariaLabel={annotation.name}
      id={auxParams.parentName + "_" + (annotation.id || annotation.name)}
      iconProps={themeUtils.getIconProps(annotation.icon as IIcon)}
      styles={styles}
      style={{
        ...themeStyle,
        color: auxParams.theme.officeTheme.semanticColors.link,
        ...style,
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={async (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (annotation.action) {
          await auxParams.actionBuilder.buildAction(annotation.action!, {
            signals: auxParams.signals,
            actionParameters: annotation.actionParameters,
          });
        } else if (annotation.ajaxAction) {
          const response = await auxParams.actionBuilder.buildAjaxAction(annotation.ajaxAction);

          if (auxParams.handleResponse && annotation.ajaxAction.response) {
            await auxParams.handleResponse(
              annotation.ajaxAction.response,
              response,
              annotation.isStandalone
            );
          }
        }
      }}
    >
      {renderButtonContent(annotation.text, annotation.image)}
    </ActionButton>
  );

  if (annotation.iconAtRight) {
    button = <div className={css.m0}>{button}</div>;
  }

  return button;
};

const commandbarbutton = (
  annotation: ICommandBarButton,

  auxParams: IAuxRenderParams
): JSX.Element => {
  const getMenuProps = () => {
    const menuProps = annotation.actions!.map<IContextualMenuItem>((a) => ({
      key: a.text!,
      text: a.text,
      action: a.action,
      onClick: (e, item) => {
        e?.preventDefault();
        e?.stopPropagation();
        auxParams.actionBuilder.buildAction(item!.action!, { signals: auxParams.signals });
      },
    }));
    return { items: menuProps };
  };

  const { style } = getButtonVisibilityStyle(annotation.permittedRole, auxParams.userRoles);

  const styles = getButtonStyles(annotation);
  const additionalStyles = {
    color: auxParams.theme.officeTheme.semanticColors.link,
    width: "auto !important",
    ...style,
  };
  styles.root = { ...(styles.root as {}), ...(additionalStyles as {}) };

  return (
    <CommandBarButton
      text={annotation.text}
      ariaLabel={annotation.text}
      disabled={annotation.disabled}
      key={annotation.name}
      id={annotation.id || annotation.name}
      iconProps={themeUtils.getIconProps(annotation.icon)}
      menuProps={getMenuProps()}
      styles={styles}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    />
  );
};

const runnablebutton = (
  annotation: IRunnableButton,

  auxParams: IAuxRenderParams
): JSX.Element => {
  const { style } = getButtonVisibilityStyle(annotation?.permittedRole, auxParams?.userRoles);

  return (
    <RunnableButton
      style={style}
      {...annotation}
      onRefresh={() => auxParams.signals?.send("refresh")}
    />
  );
};

const getButtonVisibilityStyle = (permittedRole?: string, userRoles?: string[]) => {
  const style: Partial<CSSProperties> = {};
  if (permittedRole && !userRoles?.includes(permittedRole)) {
    style.display = "none";
  }
  return { style };
};

const getButtonStyles = (a: ICommandBarButton | IActionButton) => {
  const cssStyle = themeUtils.getCSSStyle(a);

  const iconStyle = (a?.icon?.fontSize || a?.icon?.fontWeight) && themeUtils.getCSSStyle(a.icon);
  let additionalStyle: IStyle | undefined = undefined;
  if (a.theme === "externalLink") {
    additionalStyle = { padding: 0 };
  }

  const buttonStyles: IButtonStyles = {
    icon: iconStyle as any,
    root: { height: 40, ...additionalStyle },
    label: cssStyle as any,
  };

  if (a.iconAtRight) {
    buttonStyles.flexContainer = { flexDirection: "row-reverse" };
  }

  return buttonStyles;
};

export const buttonRenderers = {
  button,
  submitbutton,
  actionbutton,
  commandbarbutton,
  runnablebutton,
};

export type buttonTypes = keyof typeof buttonRenderers;
